import React from 'react';
import { utcToZonedTime, format as formatDate } from 'date-fns-tz';
import { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from 'hooks';
import {
  Thumbnail,
  Text,
  PriceTag,
  Skeleton,
  Contact,
  BasicListingTable,
} from 'components';
import { capitalize } from 'utils';
import { OpenHouse } from './types';
import {
  StyledIgnoreCursor,
  StyledMobileCell,
  StyledCircle,
} from './StyledComponents';

import MyBrokerageContext from './MyBrokerageContext';

const columns: ColumnsType<OpenHouse> = [
  {
    title: 'Address',
    dataIndex: 'line1',
    key: 'line1',
    width: 220,
    render: (line1, { thumbnail, line2, city, province, postal_code }) => (
      <StyledMobileCell>
        <Thumbnail src={thumbnail} />
        <Text strong ellipsis>
          {line1} {line2}
        </Text>
        <div>
          <Text type="secondary" ellipsis>
            {city}, {province}, {postal_code}
          </Text>
        </div>
      </StyledMobileCell>
    ),
  },
  {
    title: 'Price',
    dataIndex: 'list_price',
    key: 'list_price',
    ellipsis: true,
    width: 150,
    render: (price: number) => (
      <StyledIgnoreCursor>
        <PriceTag price={price} />
      </StyledIgnoreCursor>
    ),
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 160,
    render: (date, { timezone }) =>
      formatDate(utcToZonedTime(date, timezone), 'MMM d, yyyy'),
  },
  {
    title: 'Starts',
    dataIndex: 'timeFrom',
    key: 'timeFrom',
    width: 100,
    render: (timeFrom, { timezone }) =>
      formatDate(utcToZonedTime(timeFrom, timezone), 'h:mm a'),
  },
  {
    title: 'Ends',
    dataIndex: 'timeTo',
    key: 'timeTo',
    width: 100,
    render: (timeTo, { timezone }) =>
      formatDate(utcToZonedTime(timeTo, timezone), 'h:mm a'),
  },
];

const OpenHouses: React.FC = () => {
  const {
    results: { openHouses },
    loading,
    setRedirect,
    queryData,
    showOpenHouses,
    heightOffset,
    selectedRowKeys,
    setSelectedRowKeys,
  } = React.useContext(MyBrokerageContext);
  const { md } = useBreakpoint();

  const preparedDataSource = React.useMemo(() => {
    interface PreparedOpenHouses extends OpenHouse {
      key: number;
    }
    const formattedTerm = queryData.searchTerm.toUpperCase().trim();
    return openHouses
      .filter(({ line1, line2, city, postal_code, province, list_price }) => {
        if (!formattedTerm) return true;
        return [
          line1,
          line2,
          city,
          postal_code,
          province,
          list_price.toString(),
        ].some(value => value?.toUpperCase().includes(formattedTerm));
      })
      .map<PreparedOpenHouses>((openHouse, key) => {
        const { timeFrom, timeTo } = openHouse;
        return {
          ...openHouse,
          key,
          date: new Date(timeFrom),
          timeFrom: new Date(timeFrom),
          timeTo: new Date(timeTo),
        };
      });
  }, [openHouses, queryData]);

  const preparedColumns = React.useMemo(() => {
    if (!md) {
      if (!loading) {
        return [
          {
            title: '',
            dataIndex: 'line1',
            key: 'line1',
            render: (line1, { thumbnail, list_price, date, type }) => (
              <StyledMobileCell>
                <Thumbnail src={thumbnail} />
                <Text strong ellipsis>
                  {line1}
                </Text>
                <div>
                  <Text ellipsis>
                    <PriceTag price={list_price} />
                    <Text type="secondary">
                      {formatDate(date, 'MMM d, yyyy')}
                    </Text>
                    <StyledCircle type="circle" width={5} />
                    <Text type="secondary">{capitalize(type)}</Text>
                  </Text>
                </div>
              </StyledMobileCell>
            ),
          },
        ] as ColumnsType<OpenHouse>;
      }
      return [
        {
          title: '',
          dataIndex: 'line1',
          key: 'line1',
          render: () => (
            <StyledMobileCell>
              <Thumbnail loading />
              <div>
                <Skeleton
                  title={false}
                  paragraph={{ rows: 2, width: [150, 220] }}
                />
              </div>
            </StyledMobileCell>
          ),
        },
      ] as ColumnsType<OpenHouse>;
    }
    if (!loading) return columns;
    return columns.map(column => {
      const { key } = column;
      if (['timeTo', 'timeFrom', 'date'].includes(key.toString())) {
        return {
          ...column,
          render: () => (
            <Skeleton title={false} paragraph={{ rows: 1, width: 80 }} />
          ),
        };
      }
      if (key === 'line1') {
        return {
          ...column,
          render: () => (
            <StyledMobileCell>
              <Thumbnail loading />
              <Skeleton
                title={false}
                paragraph={{ rows: 2, width: ['100%', '90%'] }}
              />
            </StyledMobileCell>
          ),
        };
      }
      if (['neighborhood', 'status'].includes(key.toString())) {
        return {
          ...column,
          render: () => (
            <Skeleton
              title={false}
              paragraph={{ rows: 2, width: ['60%', '90%'] }}
            />
          ),
        };
      }
      if (key === 'fullReps') {
        return {
          ...column,
          render: () => (
            <>
              <Contact loading />
              <Contact loading />
            </>
          ),
        };
      }
      if (key === 'thumbnail') {
        return {
          ...column,
          render: () => <Thumbnail loading />,
        };
      }
      if (key === 'list_price') {
        return {
          ...column,
          render: () => <PriceTag loading />,
        };
      }
      return column;
    });
  }, [loading, queryData, md]);

  if (!showOpenHouses) return null;

  return (
    <BasicListingTable
      handleRowClick={key =>
        setRedirect(`/listing/${preparedDataSource[Number(key)].id}/view`)
      }
      dataSource={preparedDataSource}
      columns={preparedColumns}
      loading={loading}
      heightOffset={heightOffset}
      selectedRowKeys={selectedRowKeys}
      setSelectedRowKeys={setSelectedRowKeys}
    />
  );
};

export default OpenHouses;
